import axios from "axios";
import { serverPath } from "./key";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import Swal from 'sweetalert2';
import i18next from "i18next";

export const Network = () => {
  return axios.create({
    baseURL: serverPath,
  });
};

let refreshTokenPromise = null;

async function refreshToken(
  method,
  endpoint,
  data = {},
  headers,
  publicRequest = false
) {
  if (!refreshTokenPromise) {
    const refToken = localStorage.getItem("refreshToken");
    refreshTokenPromise = axios({
      method: "get",
      url: `${serverPath}/users/refresh`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refToken}`,
        "Access-Control-Allow-Origin": "*",
        crossorigin: true,
      },
    })
      .then((res) => {
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("refreshToken", res.data.refreshToken);
        return res.data.token;
      })
      .catch((error) => {
        console.log(error);
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userName");
        localStorage.removeItem("userId");
        localStorage.removeItem("role"); 
        // Show logout notification
        Swal.fire({
          title: i18next.t("Session Expired"),
          html: i18next.t("you have been logged out due to inactivity."),
          icon: "info",
          confirmButtonText: i18next.t("OK"),
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            popup: "swal2-front",
            htmlContainer: "my-swal-text",
            confirmButton: "black-btn",
          },
          willClose: () => {
            // Redirect after modal is closed
            window.location.href = "/";
            location.reload();
          },
        });
        throw error;
      })
      .finally(() => {
        refreshTokenPromise = null;
      });
  }
  // Wait for the shared refresh to complete
  await refreshTokenPromise;

  // Re-call the original request
  return userInstance(method, endpoint, data, headers, publicRequest);
}

export const userInstance = async (
  method,
  endpoint,
  data = {},
  headers,
  publicRequest = false
) => {
  return new Promise((resolve, reject) => {
    let token = localStorage.getItem("token");
    if (!token || token === "undefined") {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userName");
      localStorage.removeItem("userId");
      localStorage.removeItem("role");
    }
    if (!publicRequest) {
      let decodedToken;
      try {
        decodedToken = jwt_decode(token);
      } catch (e) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userName");
        localStorage.removeItem("userId");
        localStorage.removeItem("role");
      }
      const dateNow = new Date();
      if (decodedToken.exp < dateNow.getTime() / 1000) {
        resolve(refreshToken(method, endpoint, data, headers, publicRequest));
      } else {
        const token = localStorage.getItem("token");
        if (method === "GET") {
          axios({
            method: "get",
            url: `${serverPath}/${endpoint}`,
            headers: {
              "Content-Type": "application/json",
              Authorization: (token && `Bearer ${token}`) || null,
              "Access-Control-Allow-Origin": "*",
              crossorigin: true,
            },
          })
            .then((res) => {
              if (res.status >= 200 && res.status < 400) {
                resolve(res);
              } else {
                throw new Error("Http response code is: " + res.status);
              }
            })
            .catch(function (error) {
              toast.error(error.response.data.message, {
                toastId: "error",
              });
              console.log(error.toJSON());
              return Promise.reject(error);
            });
        } else {
          axios({
            method: "post",
            url: `${serverPath}/${endpoint}`,
            data,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: (token && `Bearer ${token} `) || null,
              ...headers, // Merge extra headers
            },
          })
            .then((res) => {
              if (res.status >= 200 && res.status < 400) {
                resolve(res);
              } else {
                throw new Error("Http response code is: " + res.status);
              }
            })
            .catch(function (error) {
              toast.error(error.response.data, {
                toastId: "error",
              });
              console.log(error.toJSON());
              return reject(error);
            });
        }
      }
    } else {
      if (method === "GET") {
        axios({
          method: "get",
          url: `${serverPath}/${endpoint}`,
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            crossorigin: true,
          },
        })
          .then((res) => {
            if (res.status >= 200 && res.status < 400) {
              resolve(res);
            } else {
              throw new Error("Http response code is: " + res.status);
            }
          })
          .catch(function (error) {
            toast.error(error.response.data.message);
            console.log(error.toJSON());
            return Promise.reject(error);
          });
      } else {
        axios({
          method: "post",
          url: `${serverPath}/${endpoint}`,
          data,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...headers, // Merge extra headers
          },
        })
          .then((res) => {
            if (res.status >= 200 && res.status < 400) {
              resolve(res);
            } else {
              throw new Error("Http response code is: " + res.status);
            }
          })
          .catch(function (error) {
            const errorMessage =
              error?.response?.data?.message || error?.message;
            if (errorMessage) {
              if (headers?.suppressToast === true) {
                return reject(error); // don't show toast
              }
              toast.error(errorMessage, {
                toastId: "error",
              });
            }
            console.log(error.toJSON());
            return reject(error);
          });
      }
    }
  });
};

let refreshPromise = null;

export const authenticatedRequest = async (config) => {
  const clearAuthData = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userName");
    localStorage.removeItem("userId");
    localStorage.removeItem("role");
  };

  const showSessionExpiredModal = async () => {
    await Swal.fire({
      title: i18next.t("Session Expired"),
      html: i18next.t("you have been logged out due to inactivity."),
      icon: 'info',
      confirmButtonText: i18next.t("OK"),
      customClass: {
        popup: "swal2-front",
        htmlContainer: "my-swal-text",
        confirmButton: "black-btn",
      },
      allowOutsideClick: false,
    });
    window.location.assign('/login');
  };

    const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  };

  const refreshTokens = async () => {
    if (refreshPromise) {
      return refreshPromise;
    }
    const refToken = localStorage.getItem("refreshToken");
    if (!refToken) {
      clearAuthData();
      await showSessionExpiredModal();
      throw new Error("No refresh token available");
    }

    refreshPromise = Network().get("/users/refresh", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refToken}`,
      },
    })
    .then((response) => {
      if (response.status !== 200) {
        throw new Error("Failed to refresh token");
      }
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("refreshToken", response.data.refreshToken);
      return response.data.token;
    })
    .catch(async (error) => {
      console.error("Token refresh failed:", error);
      clearAuthData();
      await showSessionExpiredModal();
      throw error;
    })
    .finally(() => {
      refreshPromise = null; 
    });
    return refreshPromise;
  };


   try {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    if (!token || !refreshToken) {
      clearAuthData();
      await showSessionExpiredModal();
      throw new Error("No tokens available");
    }

    let authToken = token;
    if (isTokenExpired(token) && !isTokenExpired(refreshToken)) {
      authToken = await refreshTokens();
    } 
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${authToken}`,
    };

    const response = await Network()(config);
    return response;
  } catch (error) {
    // If we get a 401 even after refreshing, clear auth data and show modal
    if (error.response?.status === 401) {
      clearAuthData();
      await showSessionExpiredModal();
    }
    throw error;
  }
};